import React, { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  Lightbulb,
  ChevronDown,
  User,
  Mail,
  Briefcase,
  Building,
  Users,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { IoIosCloseCircle } from "react-icons/io";
import { useSelector } from "react-redux";
import {
  useApplyLeaveManuallyAsManagerMutation,
  useGetManagerReviewBundleQuery,
  useGetCfmReviewBundleQuery,
} from "../../slices/leaveApiSlice";
import {
  useGetUsersQuery,
  useGetDepartmentByIdQuery,
  useGetDivisionByIdQuery,
} from "../../slices/userApiSlice";

// ✅ FIXED: Enhanced helper function to check if dates overlap
const hasDateOverlap = (startDate1, endDate1, startDate2, endDate2) => {
  try {
    if (!startDate1 || !endDate1 || !startDate2 || !endDate2) return false;

    const start1 = new Date(startDate1);
    const end1 = new Date(endDate1);
    const start2 = new Date(startDate2);
    const end2 = new Date(endDate2);

    if (
      isNaN(start1.getTime()) ||
      isNaN(end1.getTime()) ||
      isNaN(start2.getTime()) ||
      isNaN(end2.getTime())
    ) {
      return false;
    }

    // Reset time part to avoid timezone issues
    start1.setHours(0, 0, 0, 0);
    end1.setHours(0, 0, 0, 0);
    start2.setHours(0, 0, 0, 0);
    end2.setHours(0, 0, 0, 0);

    // Check for overlap using inclusive range comparison
    return (
      (start1 >= start2 && start1 <= end2) || // New start date falls within existing range
      (end1 >= start2 && end1 <= end2) || // New end date falls within existing range
      (start1 <= start2 && end1 >= end2) // New range completely encompasses existing range
    );
  } catch (error) {
    console.error("Error in hasDateOverlap:", error);
    return false;
  }
};

// ✅ FIXED: Helper function to calculate days between dates
const calculateDaysBetween = (startDate, endDate) => {
  try {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    // Reset time part to avoid timezone issues
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Check if end date is before start date
    if (end < start) {
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return -diffDays; // Return negative for invalid date range
    }

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  } catch (error) {
    console.error("Error in calculateDaysBetween:", error);
    return 0;
  }
};

// Error Overlay Component
const ErrorOverlay = ({ message, onClose, title = "Error", debugInfo }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white p-6 rounded-2xl shadow-md w-90 max-w-md relative">
        <div className="text-center mb-4">
          <h2 className="text-xl font-bold text-red-600">{title}</h2>
        </div>
        <button onClick={onClose} className="absolute top-3 right-3">
          <IoIosCloseCircle
            style={{ color: "#897463", width: "24px", height: "24px" }}
          />
        </button>

        <div className="text-center">
          <p className="text-gray-700 mb-4">{message}</p>
          {debugInfo && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4 text-left">
              <p className="text-sm text-yellow-800">
                <strong>Debug Info:</strong> {debugInfo}
              </p>
            </div>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Component to fetch and display department name
const DepartmentName = ({ departmentId }) => {
  const {
    data: department,
    isLoading,
    error,
  } = useGetDepartmentByIdQuery(departmentId, {
    skip: !departmentId,
  });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (error || !department)
    return <span className="text-gray-500">Unknown Dept</span>;

  return <span>{department.name}</span>;
};

// Component to fetch and display division name
const DivisionName = ({ divisionId }) => {
  const {
    data: division,
    isLoading,
    error,
  } = useGetDivisionByIdQuery(divisionId, {
    skip: !divisionId,
  });

  if (isLoading) return <span className="text-gray-500">Loading...</span>;
  if (error || !division)
    return <span className="text-gray-500">No Division</span>;

  return <span>{division.name}</span>;
};

// Fixed helper function to get role name
const getRoleName = (user) => {
  if (!user || !user.role) return "user";
  if (typeof user.role === "object" && user.role.name) {
    return user.role.name;
  }
  if (typeof user.role === "string") {
    return user.role;
  }
  return "user";
};

// Enhanced SafeRoleDisplay component with manager highlight
const SafeRoleDisplay = ({ user }) => {
  const roleName = getRoleName(user);
  const roleColors = {
    supervisor: "bg-green-100 text-green-800",
    technician: "bg-blue-100 text-blue-800",
    manager: "bg-purple-100 text-purple-800 border border-purple-300",
    cfm: "bg-red-100 text-red-800",
    default: "bg-gray-100 text-gray-800",
  };

  const colorClass = roleColors[roleName.toLowerCase()] || roleColors.default;

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full capitalize ${colorClass}`}
    >
      {roleName.toLowerCase()}
      {roleName.toLowerCase() === "manager"}
    </span>
  );
};

// ✅ FIXED: Component to display conflicting leaves
const ConflictLeavesList = ({ conflicts, selectedUser }) => {
  if (!conflicts || conflicts.length === 0) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="rounded-lg p-1">
      <div className="flex items-center mt--5">
        <span className="text-red-400 font-medium text-sm">
          You already have leave for the selected dates
        </span>
      </div>
    </div>
  );
};

export default function ApplyManualLeave() {
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);
  const usersDropdownRef = useRef(null);

  // Get manager info from Redux store
  const { userInfo, userToken } = useSelector((state) => state.auth);

  // Enhanced manager info extraction with multiple fallbacks
  const managerId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const managerEmail =
    userInfo?.email || userInfo?.userEmail || userInfo?.username;
  const managerName = userInfo?.name || userInfo?.userName || "Manager";

  // FIXED: Get department ID with comprehensive fallbacks
  const managerDepartmentId =
    userInfo?.departmentId || userInfo?.department?.id || userInfo?.department;

  // NEW: Get academy ID with comprehensive fallbacks
  const managerAcademyId =
    userInfo?.academyId || userInfo?.academy?.id || userInfo?.academy;

  // RTK Query mutation for applying manual leave
  const [applyLeaveManually, { isLoading: isSubmitting }] =
    useApplyLeaveManuallyAsManagerMutation();

  // Fetch users - with better error handling
  const {
    data: usersData = [],
    isLoading: isLoadingUsers,
    error: usersError,
  } = useGetUsersQuery();

  // ✅ ADD THIS: Fetch CFM review bundle to get ALL leaves including manager leaves
  const {
    data: cfmReviewBundleResponse,
    isLoading: isLoadingCfmLeaves,
    error: cfmLeavesError,
    refetch: refetchCfmLeaves,
  } = useGetCfmReviewBundleQuery(
    { cfmId: managerId, academyId: managerAcademyId },
    { skip: !managerId || !managerAcademyId },
  );

  // Fetch existing leaves from manager review bundle (for technicians/supervisors)
  const {
    data: reviewBundleResponse,
    isLoading: isLoadingLeaves,
    error: leavesError,
    refetch: refetchLeaves,
  } = useGetManagerReviewBundleQuery(
    { managerId, departmentId: managerDepartmentId },
    { skip: !managerId || !managerDepartmentId },
  );

  const [formData, setFormData] = useState({
    employeeId: "",
    employeeName: "",
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    selectedUser: null,
  });

  const [errors, setErrors] = useState({});
  const [showErrorOverlay, setShowErrorOverlay] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [debugInfo, setDebugInfo] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);

  // State to track existing leaves for date validation
  const [existingLeaves, setExistingLeaves] = useState([]);

  // ✅ NEW: State to track conflicting leaves for display
  const [conflictingLeaves, setConflictingLeaves] = useState([]);

  // Common leave type suggestions
  const leaveTypeSuggestions = [
    "Medical Leave",
    "Emergency Leave",
    "Maternity Leave",
    "Paternity Leave",
    "Study Leave",
    "Sabbatical Leave",
    "Compensatory Leave",
    "Vacation Leave",
    "Sick Leave",
    "Personal Leave",
    "Bereavement Leave",
    "Marriage Leave",
    "Pilgrimage Leave",
    "Manual Leave",
  ];

  // Filter users by academy and roles - MANAGERS FROM SAME ACADEMY
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    if (usersData && Array.isArray(usersData)) {
      let filtered = usersData;

      console.log("🔍 CFM USER FILTERING DEBUG:", {
        totalUsers: usersData.length,
        managerAcademyId,
        managerId,
        managerRole: userInfo?.role,
      });

      // Filter 1: Get users from same academy ONLY
      if (managerAcademyId) {
        filtered = filtered.filter(
          (user) => user.academyId === managerAcademyId,
        );
        console.log(
          `🎓 After academy filter (${managerAcademyId}):`,
          filtered.length,
        );
      } else {
        console.warn("⚠️ No academy ID available for CFM");
        setFilteredUsers([]);
        return;
      }

      // Filter 2: Include ONLY managers (CFM can apply leave for other managers in same academy)
      filtered = filtered.filter((user) => {
        const userRole = getRoleName(user).toUpperCase();
        return userRole === "MANAGER";
      });
      console.log("👥 After role filter (Managers only):", filtered.length);

      // Filter 3: Exclude current CFM themselves
      filtered = filtered.filter((user) => user.userId !== managerId);
      console.log("👤 After self exclusion:", filtered.length);

      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [usersData, managerAcademyId, managerId, userInfo]);

  // ✅ ENHANCED: Process leaves from BOTH manager and CFM review bundles
  const processAllLeavesData = () => {
    const allLeaves = [];

    // Process leaves from manager review bundle (technicians/supervisors)
    if (reviewBundleResponse) {
      allLeaves.push(
        ...(reviewBundleResponse.technicianLeaves || []),
        ...(reviewBundleResponse.supervisorLeaves || []),
      );
    }

    // ✅ ADD THIS: Process leaves from CFM review bundle (includes manager leaves)
    if (cfmReviewBundleResponse) {
      allLeaves.push(
        ...(cfmReviewBundleResponse.technicianLeaves || []),
        ...(cfmReviewBundleResponse.supervisorLeaves || []),
        ...(cfmReviewBundleResponse.managerLeaves || []), // ✅ THIS IS WHAT WE NEED!
      );
    }

    console.log("📊 Processing ALL leave data from both bundles:", {
      fromManagerBundle: reviewBundleResponse
        ? (reviewBundleResponse.technicianLeaves?.length || 0) +
          (reviewBundleResponse.supervisorLeaves?.length || 0)
        : 0,
      fromCfmBundle: cfmReviewBundleResponse
        ? (cfmReviewBundleResponse.technicianLeaves?.length || 0) +
          (cfmReviewBundleResponse.supervisorLeaves?.length || 0) +
          (cfmReviewBundleResponse.managerLeaves?.length || 0)
        : 0,
      totalLeaves: allLeaves.length,
    });

    return allLeaves.map((leave) => ({
      id: leave.id || leave._id,
      userId: leave.userId,
      employeeEmail:
        leave.employeeEmail ||
        leave.Email ||
        leave.userEmail ||
        (leave.userId &&
        typeof leave.userId === "string" &&
        leave.userId.includes("@")
          ? leave.userId
          : ""),
      leaveType: leave.leaveType,
      startDate: leave.startDate,
      endDate: leave.endDate,
      status: leave.status,
      duration: leave.duration,
      reason: leave.reason,
      applicantRole: leave.applicantRole, // Add role info for better filtering
    }));
  };

  // ✅ ENHANCED: Load existing leaves from BOTH sources
  useEffect(() => {
    const processedLeaves = processAllLeavesData();
    console.log(
      "📅 Loaded ALL existing leaves from both bundles:",
      processedLeaves,
    );

    // Log manager leaves specifically for debugging
    const managerLeaves = processedLeaves.filter(
      (leave) =>
        leave.applicantRole === "MANAGER" ||
        (leave.userId &&
          filteredUsers.some((user) => user.userId === leave.userId)),
    );
    console.log("👥 Manager leaves found:", managerLeaves);

    setExistingLeaves(processedLeaves);
  }, [reviewBundleResponse, cfmReviewBundleResponse, filteredUsers]);

  // ✅ FIXED: Check for leave conflicts whenever dates or selected user changes
  useEffect(() => {
    if (formData.selectedUser && formData.startDate && formData.endDate) {
      try {
        const conflicts = checkLeaveConflict();
        setConflictingLeaves(conflicts);
      } catch (error) {
        console.error("Error checking leave conflict:", error);
        setConflictingLeaves([]);
      }
    } else {
      setConflictingLeaves([]);
    }
  }, [
    formData.selectedUser,
    formData.startDate,
    formData.endDate,
    existingLeaves,
  ]);

  // Show error overlay function
  const showError = (message, debug = "") => {
    setErrorMessage(message);
    setDebugInfo(debug);
    setShowErrorOverlay(true);
  };

  // Hide error overlay function
  const hideError = () => {
    setShowErrorOverlay(false);
    setErrorMessage("");
    setDebugInfo("");
  };

  const handleBack = () => {
    navigate("/cfm/attendance");
  };

  // ✅ FIXED: Enhanced handleInputChange for dates
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for date inputs
    if (name === "startDate" || name === "endDate") {
      // Clear previous errors for this field
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }

      // Clear conflict errors if date is changed
      if (errors.leaveType && errors.leaveType.includes("Cannot apply leave")) {
        setErrors((prev) => ({
          ...prev,
          leaveType: "",
          startDate: "",
          endDate: "",
        }));
      }

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // If start date changed and end date is before it, clear end date
      if (
        name === "startDate" &&
        formData.endDate &&
        value > formData.endDate
      ) {
        setFormData((prev) => ({
          ...prev,
          endDate: "",
        }));
      }

      return;
    }

    // Regular handling for other inputs
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "leaveType") {
      setShowSuggestions(true);
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      leaveType: suggestion,
    }));
    setShowSuggestions(false);
  };

  const handleLeaveTypeFocus = () => {
    setShowSuggestions(true);
  };

  const handleLeaveTypeBlur = (e) => {
    if (
      suggestionsRef.current &&
      suggestionsRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    setShowSuggestions(false);
  };

  const handleSuggestionMouseDown = (e) => {
    e.preventDefault();
  };

  // User selection handlers
  const handleUsersDropdownToggle = () => {
    setShowUsersDropdown(!showUsersDropdown);
  };

  const handleUserSelect = (user) => {
    console.log("👤 User selected:", user);

    if (!user || !user.userId) {
      showError("Invalid employee selection");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      employeeId: user.userId,
      employeeName: user.name || "Unknown User",
      selectedUser: user,
    }));

    setShowUsersDropdown(false);
    setErrors((prev) => ({ ...prev, employeeId: "" }));

    // ✅ Clear conflicting leaves when user changes
    setConflictingLeaves([]);

    // Refetch leaves to get the latest data
    refetchLeaves();
    refetchCfmLeaves();
  };

  const handleUsersDropdownBlur = (e) => {
    if (
      usersDropdownRef.current &&
      usersDropdownRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    setShowUsersDropdown(false);
  };

  // Get full name from user object
  const getUserFullName = (user) => {
    return `${user.name}`.trim() || user.email;
  };

  // ✅ FIXED: calculateDays function with better error handling
  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) {
      return 0;
    }

    try {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 0;
      }

      // Reset time part to avoid timezone issues
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      // Check if end date is before start date
      if (end < start) {
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return -diffDays; // Return negative for invalid date range
      }

      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    } catch (error) {
      console.error("Error calculating days:", error);
      return 0;
    }
  };

  // ✅ FIXED: Enhanced checkLeaveConflict function with better error handling
  const checkLeaveConflict = () => {
    try {
      if (!formData.selectedUser || !formData.startDate || !formData.endDate) {
        return [];
      }

      console.log("🔍 Checking leave conflict for:", {
        employee: getUserFullName(formData.selectedUser),
        employeeId: formData.selectedUser.userId,
        newDates: `${formData.startDate} to ${formData.endDate}`,
        totalExistingLeaves: existingLeaves.length,
      });

      // Validate dates first
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error("❌ Invalid dates detected");
        return [];
      }

      // Filter leaves for this user with relevant statuses
      const userLeaves = existingLeaves.filter((leave) => {
        // Validate leave dates
        if (!leave.startDate || !leave.endDate) return false;

        try {
          const leaveStart = new Date(leave.startDate);
          const leaveEnd = new Date(leave.endDate);
          if (isNaN(leaveStart.getTime()) || isNaN(leaveEnd.getTime()))
            return false;
        } catch (err) {
          return false;
        }

        // Enhanced user matching
        const isUserMatch =
          (leave.userId && leave.userId === formData.selectedUser.userId) ||
          (leave.employeeEmail &&
            leave.employeeEmail.toLowerCase() ===
              formData.selectedUser.email.toLowerCase()) ||
          (leave.userEmail &&
            leave.userEmail.toLowerCase() ===
              formData.selectedUser.email.toLowerCase());

        // Only consider leaves that are approved, pending, or forwarded
        const isRelevantStatus =
          leave.status &&
          ["APPROVED", "PENDING", "FORWARDED"].includes(leave.status);

        return isUserMatch && isRelevantStatus;
      });

      console.log(
        `📋 Found ${userLeaves.length} relevant leaves for conflict check`,
      );

      // Find all conflicting leaves with proper date validation
      const conflicts = userLeaves.filter((leave) => {
        try {
          const overlap = hasDateOverlap(
            formData.startDate,
            formData.endDate,
            leave.startDate,
            leave.endDate,
          );
          return overlap;
        } catch (error) {
          console.error(
            "❌ Error checking overlap for leave:",
            leave.id,
            error,
          );
          return false;
        }
      });

      if (conflicts.length > 0) {
        console.log(`❌ Found ${conflicts.length} conflicting leave(s)`);
      } else {
        console.log("✅ No conflicts found");
      }

      return conflicts;
    } catch (error) {
      console.error("❌ Error in checkLeaveConflict:", error);
      return [];
    }
  };

  // Check if there are any conflicts (for form validation)
  const hasLeaveConflict = conflictingLeaves.length > 0;

  // ✅ FIXED: Enhanced date validation function
  const validateDates = () => {
    const newErrors = {};

    if (!formData.startDate) {
      newErrors.startDate = "Please select a start date";
    }

    if (!formData.endDate) {
      newErrors.endDate = "Please select an end date";
    }

    if (formData.startDate && formData.endDate) {
      try {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          newErrors.startDate = "Invalid date format";
          newErrors.endDate = "Invalid date format";
        } else {
          // Reset time part for accurate comparison
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);

          if (end < start) {
            newErrors.endDate = "End date cannot be before start date";
          }

          // Check if leave spans more than 30 days
          const diffTime = Math.abs(end - start);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

          if (diffDays > 30) {
            newErrors.endDate =
              "Leaves longer than 30 days require special approval";
          }
        }
      } catch (error) {
        console.error("Error validating dates:", error);
        newErrors.startDate = "Error validating dates";
        newErrors.endDate = "Error validating dates";
      }
    }

    return newErrors;
  };

  // ✅ FIXED: Enhanced validation function
  const validateForm = () => {
    const newErrors = {};

    // Validate employee selection
    if (
      !formData.employeeId ||
      !formData.employeeId.trim() ||
      !formData.selectedUser
    ) {
      newErrors.employeeId = "Please select an employee";
    }

    // Validate leave type
    if (!formData.leaveType || !formData.leaveType.trim()) {
      newErrors.leaveType = "Please enter or select a leave type";
    }

    // Validate dates
    const dateErrors = validateDates();
    Object.assign(newErrors, dateErrors);

    // Validate reason
    if (!formData.reason || !formData.reason.trim()) {
      newErrors.reason = "Please provide a reason for leave";
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason =
        "Please provide a more detailed reason (minimum 10 characters)";
    }

    // Check for conflicts if all date fields are valid
    if (
      !newErrors.startDate &&
      !newErrors.endDate &&
      formData.startDate &&
      formData.endDate
    ) {
      try {
        const latestConflicts = checkLeaveConflict();
        if (latestConflicts.length > 0) {
          newErrors.leaveType = `Cannot apply leave - ${getUserFullName(formData.selectedUser)} already has ${latestConflicts.length} leave(s) during the selected dates.`;
          newErrors.startDate = "Date conflict detected";
          newErrors.endDate = "Date conflict detected";

          // Also update conflictingLeaves state
          setConflictingLeaves(latestConflicts);
        }
      } catch (error) {
        console.error("❌ Error checking conflicts:", error);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🔍 Starting submit process...");
    console.log("🎓 Academy Check:", {
      managerAcademyId,
      employeeAcademyId: formData.selectedUser?.academyId,
      isSameAcademy: managerAcademyId === formData.selectedUser?.academyId,
    });

    // First validate the form
    const isValid = validateForm();
    if (!isValid) {
      const firstError = Object.values(errors)[0];
      if (firstError) showError(firstError);
      return;
    }

    // ✅ ENHANCED: Re-check conflicts right before submission
    const latestConflicts = checkLeaveConflict();
    if (latestConflicts.length > 0) {
      setConflictingLeaves(latestConflicts);
      showError(
        `Cannot apply leave for the selected dates because ${getUserFullName(formData.selectedUser)} already has ${latestConflicts.length} leave(s) during this period.`,
        `Employee: ${getUserFullName(formData.selectedUser)}, Dates: ${formData.startDate} to ${formData.endDate}, Conflicts: ${latestConflicts.length}`,
      );
      return;
    }

    // FIXED: Better academy check with error handling
    if (!managerAcademyId) {
      showError(
        "Cannot determine your academy. Please ensure your CFM account has an academy assigned.",
        "CFM academy ID is undefined. Check user profile setup.",
      );
      return;
    }

    try {
      const leaveTypeMap = {
        "Medical Leave": "MEDICAL",
        "Casual Leave": "CASUAL",
        "Emergency Leave": "EMERGENCY",
        "Maternity Leave": "MATERNITY",
        "Paternity Leave": "PATERNITY",
        "Study Leave": "STUDY",
        "Sabbatical Leave": "SABBATICAL",
        "Compensatory Leave": "COMPENSATORY",
        "Vacation Leave": "VACATION",
        "Sick Leave": "SICK",
        "Personal Leave": "PERSONAL",
        "Bereavement Leave": "BEREAVEMENT",
        "Marriage Leave": "MARRIAGE",
        "Pilgrimage Leave": "PILGRIMAGE",
        "Earned Leave": "EARNED",
        "Manual Leave": "MANUAL",
      };

      const backendLeaveType = leaveTypeMap[formData.leaveType] || "CASUAL";

      // Calculate duration
      let duration = "FULL_DAY";
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays > 1) {
          duration = "MULTI_DAY";
        }
      }

      // Calculate total days for database
      const totalDays = calculateDays();

      // ✅ CORRECTED PAYLOAD - Include totalDays and force employee email to be used
      const leaveRequest = {
        // EMPLOYEE INFORMATION - The person taking the leave
        userId: formData.selectedUser.userId,
        employeeEmail: formData.selectedUser.email, // ✅ Selected employee's email
        email: formData.selectedUser.email, // ✅ Also send as 'email' field
        userEmail: formData.selectedUser.email, // ✅ And as 'userEmail' field

        // Leave details
        leaveType: backendLeaveType,
        duration: duration,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        totalDays: totalDays, // ✅ Added totalDays to payload
        reason: formData.reason.trim(),

        // Manager information - For approval tracking only
        managerId: managerId,
        managerEmail: managerEmail,
        managerName: managerName,

        // Employee role and department
        applicantRole: getRoleName(formData.selectedUser).toUpperCase(),
        departmentId: formData.selectedUser.departmentId,
        divisionId: formData.selectedUser.divisionId || null,
        academyId: formData.selectedUser.academyId || managerAcademyId,

        // Application metadata
        applicationType: "MANUAL",
        submittedBy: managerEmail, // CFM is submitting, but leave is FOR employee
      };

      // Add supervisor fields if needed
      const selectedUserRole = getRoleName(formData.selectedUser).toUpperCase();
      if (
        (selectedUserRole === "TECHNICIAN" ||
          selectedUserRole === "SUPERVISOR") &&
        formData.selectedUser.supervisorId
      ) {
        leaveRequest.supervisorId = formData.selectedUser.supervisorId;
        leaveRequest.supervisorEmail = formData.selectedUser.supervisorEmail;
      }

      // ✅ FIXED: Call API and handle response properly
      try {
        const result = await applyLeaveManually(leaveRequest).unwrap();

        console.log(
          "✅ SUCCESS - Leave applied for employee:",
          formData.selectedUser.email,
        );

        // Show success and reset form
        toast.success("Leave Applied Successfully!", {
          description: `${formData.leaveType} has been successfully applied for ${getUserFullName(formData.selectedUser)}`,
        });

        // Reset form and navigate after success
        setFormData({
          employeeId: "",
          employeeName: "",
          leaveType: "",
          startDate: "",
          endDate: "",
          reason: "",
          selectedUser: null,
        });
        setErrors({});
        setConflictingLeaves([]);

        // Refetch ALL data to get updated leaves
        refetchLeaves();
        refetchCfmLeaves(); // ✅ ADD THIS
        navigate("/cfm/attendance/employee-leave-requests");
      } catch (error) {
        console.error("❌ API ERROR:", error);
        showError(
          "Failed to submit leave application. Please try again.",
          `Error: ${error?.data?.message || error?.error || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("❌ UNEXPECTED ERROR:", error);
      showError("An unexpected error occurred. Please try again.");
    }
  };

  const today = new Date().toISOString().split("T")[0];
  const totalDays = calculateDays();

  // Filter suggestions based on current input
  const filteredSuggestions = leaveTypeSuggestions.filter((suggestion) =>
    suggestion.toLowerCase().includes(formData.leaveType.toLowerCase()),
  );

  return (
    <div className="p-8 max-w-10xl mx-auto">
      {/* Error Overlay */}
      {showErrorOverlay && (
        <ErrorOverlay
          message={errorMessage}
          debugInfo={debugInfo}
          onClose={hideError}
        />
      )}

      {/* Header */}
      <div className="flex items-center space-x-1 mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className=" md:flex w-9 h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft size={24} strokeWidth={2} className="text-gray-700" />
        </button>
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-gray-800">
            Apply Manual Leave
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Form */}
        <div className="lg:col-span-2">
          <div className="bg-[#FAFAFA] rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Employee Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee *
                </label>
                <div className="relative" ref={usersDropdownRef}>
                  <button
                    type="button"
                    onClick={handleUsersDropdownToggle}
                    onBlur={handleUsersDropdownBlur}
                    className={`w-full border-2 rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-400 transition-colors ${
                      errors.employeeId ? "border-red-500" : "border-gray-300"
                    } ${formData.selectedUser ? "text-gray-900" : "text-gray-500"}`}
                  >
                    <div className="flex items-center space-x-3 truncate">
                      {formData.selectedUser ? (
                        <>
                          <User className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          <div className="truncate">
                            <div className="font-medium text-sm">
                              {getUserFullName(formData.selectedUser)}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {formData.selectedUser.email}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span>
                          {managerAcademyId
                            ? "Select managers from your academy"
                            : "Academy not configured - cannot load employees"}
                        </span>
                      )}
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${showUsersDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showUsersDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                      {isLoadingUsers ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          Loading employees...
                        </div>
                      ) : !managerAcademyId ? (
                        <div className="px-4 py-3 text-center text-yellow-600">
                          Your CFM account doesn't have an academy assigned.
                          Please contact admin.
                        </div>
                      ) : filteredUsers.length === 0 ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          No managers found in your academy
                        </div>
                      ) : (
                        filteredUsers.map((user, index) => (
                          <button
                            key={
                              user.userId || user.employeeId || `user-${index}`
                            }
                            type="button"
                            onClick={() => handleUserSelect(user)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-purple-600" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                                    {getUserFullName(user)}
                                  </h4>
                                  <SafeRoleDisplay user={user} />
                                </div>
                                <div className="mt-1 space-y-1">
                                  <div className="flex items-center text-xs text-gray-600">
                                    <Mail className="w-3 h-3 mr-1" />
                                    <span className="truncate">
                                      {user.email}
                                    </span>
                                  </div>
                                  <div className="flex items-center text-xs text-gray-600">
                                    <Briefcase className="w-3 h-3 mr-1" />
                                    <span className="truncate">
                                      <DepartmentName
                                        departmentId={user.departmentId}
                                      />
                                    </span>
                                  </div>
                                  {user.divisionId && (
                                    <div className="flex items-center text-xs text-gray-600">
                                      <Building className="w-3 h-3 mr-1" />
                                      <span className="truncate">
                                        <DivisionName
                                          divisionId={user.divisionId}
                                        />
                                      </span>
                                    </div>
                                  )}
                                  {user.employeeId && (
                                    <div className="flex items-center text-xs text-gray-500">
                                      <Users className="w-3 h-3 mr-1" />
                                      <span>ID: {user.employeeId}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {errors.employeeId && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.employeeId}
                  </p>
                )}
                {managerAcademyId ? (
                  <p className="mt-1 text-xs text-gray-500">
                    Showing managers from your academy only
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-yellow-600">
                    ⚠️ Your CFM account doesn't have an academy assigned. Please
                    contact admin.
                  </p>
                )}
              </div>

              {/* Leave Type - Text Input with Suggestions */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Leave Type *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="leaveType"
                    placeholder="Type or select a leave type"
                    value={formData.leaveType}
                    onChange={handleInputChange}
                    onFocus={handleLeaveTypeFocus}
                    onBlur={handleLeaveTypeBlur}
                    className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${
                      errors.leaveType ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>

                {/* Suggestions Dropdown */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {filteredSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseDown={handleSuggestionMouseDown}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}

                {errors.leaveType && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.leaveType}
                  </p>
                )}
              </div>

              {/* Date Selection - FIXED */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={today}
                    className={`w-full border-2 rounded-lg px-4 py-3 
                      ${
                        errors.startDate || hasLeaveConflict
                          ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                    onFocus={() => {
                      if (
                        errors.startDate ||
                        errors.leaveType?.includes("Cannot apply leave")
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          startDate: "",
                          leaveType: errors.leaveType?.includes(
                            "Cannot apply leave",
                          )
                            ? ""
                            : prev.leaveType,
                        }));
                      }
                    }}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-600 font-medium">
                      {errors.startDate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || today}
                    className={`w-full border-2 rounded-lg px-4 py-3 
                      ${
                        errors.endDate || hasLeaveConflict
                          ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      }`}
                    onFocus={() => {
                      if (
                        errors.endDate ||
                        errors.leaveType?.includes("Cannot apply leave")
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          endDate: "",
                          leaveType: errors.leaveType?.includes(
                            "Cannot apply leave",
                          )
                            ? ""
                            : prev.leaveType,
                        }));
                      }
                    }}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-600 font-medium">
                      {errors.endDate}
                    </p>
                  )}
                </div>
              </div>

              {/* Show conflict details if any */}
              {hasLeaveConflict && (
                <div className="mt-2">
                  <ConflictLeavesList
                    conflicts={conflictingLeaves}
                    selectedUser={formData.selectedUser}
                  />
                </div>
              )}

              {/* Total Days Display */}
              {formData.startDate && formData.endDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Days
                  </label>
                  <div
                    className={`w-full border-2 rounded-lg px-4 py-3 ${
                      totalDays < 0
                        ? ""
                        : hasLeaveConflict
                          ? ""
                          : "border-gray-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={totalDays < 0 ? "text-red-600" : ""}>
                        {totalDays < 0
                          ? `Invalid date range (${Math.abs(totalDays)} days)`
                          : `${totalDays} ${totalDays === 1 ? "day" : "days"}`}
                      </span>
                      {totalDays > 30 && (
                        <span className="text-xs text-red-600 font-medium">
                          (Special approval required)
                        </span>
                      )}
                      {totalDays < 0 && (
                        <span className="text-xs text-red-600 font-medium">
                          (End date cannot be before start date)
                        </span>
                      )}
                    </div>
                  </div>
                  {totalDays < 0 && (
                    <p className="mt-1 text-sm text-red-600">
                      Please select an end date that is after the start date
                    </p>
                  )}
                </div>
              )}

              {/* Reason for Leave */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Leave *
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Please provide a detailed reason for leave request (minimum 10 characters)"
                  className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reason ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.reason && (
                  <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.selectedUser ||
                    !managerAcademyId ||
                    hasLeaveConflict ||
                    totalDays < 0 ||
                    !formData.startDate ||
                    !formData.endDate ||
                    Object.keys(errors).some(
                      (key) =>
                        key === "startDate" ||
                        key === "endDate" ||
                        key === "leaveType",
                    )
                  }
                  className="w-full bg-black text-white py-3 px-6 rounded-lg font-semibold hover:bg-zinc-800 transition-colors mb-4 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Applying Leave...</span>
                    </>
                  ) : !managerAcademyId ? (
                    <span>Academy Not Configured</span>
                  ) : hasLeaveConflict ? (
                    <span>Resolve Date Conflict First</span>
                  ) : totalDays < 0 ? (
                    <span>Fix Invalid Date Range</span>
                  ) : errors.startDate || errors.endDate ? (
                    <span>Fix Date Errors</span>
                  ) : !formData.startDate || !formData.endDate ? (
                    <span>Select Dates</span>
                  ) : (
                    <span>
                      Apply Leave for {getUserFullName(formData.selectedUser)}
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column - How it works */}
        <div className="space-y-6">
          {/* Selected Employee Preview */}
          {formData.selectedUser && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-3">
                Selected Employee
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Name:
                  </span>
                  <span className="text-sm text-green-700">
                    {getUserFullName(formData.selectedUser)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Email:
                  </span>
                  <span className="text-sm text-green-700">
                    {formData.selectedUser.email}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Role:
                  </span>
                  <span className="text-sm text-green-700 capitalize">
                    {getRoleName(formData.selectedUser).toLowerCase()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-800">
                    Department:
                  </span>
                  <span className="text-sm text-green-700">
                    <DepartmentName
                      departmentId={formData.selectedUser.departmentId}
                    />
                  </span>
                </div>
                {formData.selectedUser.divisionId && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-800">
                      Division:
                    </span>
                    <span className="text-sm text-green-700">
                      <DivisionName
                        divisionId={formData.selectedUser.divisionId}
                      />
                    </span>
                  </div>
                )}
                {formData.selectedUser.employeeId && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-green-800">
                      Employee ID:
                    </span>
                    <span className="text-sm text-green-700">
                      {formData.selectedUser.employeeId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* How it works section */}
          <div className="bg-[#FAFAFA] border border-gray-200 rounded-lg p-4">
            <div className="flex items-start space-x-2 mb-2">
              <Lightbulb className="w-5 h-5 text-black-600 mt-0.1 ml-1 flex-shrink-0" />
              <h3 className="font-semibold text-gray-800">How it works</h3>
            </div>
            <ul className="text-sm text-gray-700 space-y-3 ml-2">
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">1.</span>
                <span>Select managers from your academy</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">2.</span>
                <span>Choose appropriate leave type</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">3.</span>
                <span>Provide detailed reason for leave</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-500 mr-2">4.</span>
                <span>Leave is automatically applied for the manager</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
